"use client";

import { useState } from "react";
import { renderSlots } from "@/components/tools/template-slots";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

type EntryMode = "separate" | "paired";

const LINE_SPLIT_RE = /\r?\n/;

function parseNumbers(text: string): number[] {
  return parseNumberList(text);
}

function parsePairedLines(text: string): { xs: number[]; ys: number[] } {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const line of text.split(LINE_SPLIT_RE)) {
    const numbers = parseNumbers(line);
    if (numbers.length >= 2) {
      xs.push(numbers[0]);
      ys.push(numbers[1]);
    }
  }
  return { xs, ys };
}

interface CorrelationResult {
  n: number;
  meanX: number;
  meanY: number;
  sxy: number;
  sxx: number;
  syy: number;
  r: number;
  rSquared: number;
}

function mean(values: number[]): number {
  return values.reduce((acc, v) => acc + v, 0) / values.length;
}

function computePearson(xs: number[], ys: number[]): CorrelationResult | null {
  if (xs.length !== ys.length || xs.length < 3) {
    return null;
  }
  const meanX = mean(xs);
  const meanY = mean(ys);
  let sxy = 0;
  let sxx = 0;
  let syy = 0;
  for (let i = 0; i < xs.length; i++) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    sxy += dx * dy;
    sxx += dx * dx;
    syy += dy * dy;
  }
  if (sxx === 0 || syy === 0) {
    return null;
  }
  const r = sxy / Math.sqrt(sxx * syy);
  return { n: xs.length, meanX, meanY, sxy, sxx, syy, r, rSquared: r * r };
}

/** English strings. A translated content file overrides these through `ui`. */
export const CORRELATION_CALCULATOR_STRINGS = {
  strong: "strong",
  moderate: "moderate",
  weak: "weak",
  negligible: "negligible",
  positive: "positive",
  negative: "negative",
  flat: "flat",
  dataEntryMode: "Data entry mode",
  separateXAndYLists: "Separate X and Y lists",
  pairedOneXYPerLine: 'Paired — one "x, y" per line',
  xValues: "X values",
  yValues: "Y values",
  xValuesPlaceholder: "1, 2, 3, 4, 5",
  yValuesPlaceholder: "2, 4, 5, 4, 6",
  pairedDataLabel: "Paired data — one x,y pair per line",
  pairedDataPlaceholder: "1, 2\n2, 4\n3, 5\n4, 4\n5, 6",
  valuesCount: "one:{count} value|other:{count} values",
  pairsDetected:
    "one:{count} pair detected — lines without two numbers are ignored|other:{count} pairs detected — lines without two numbers are ignored",
  lengthsMismatch:
    "X has {xValues} but Y has {yCount} — the two lists must be the same length.",
  enterAtLeast3Pairs:
    "Enter at least 3 pairs of values to compute a correlation.",
  xyMustVary:
    "The X and Y values must each vary — a constant list has no defined correlation.",
  results: "Results",
  pearsonsR: "Pearson's r",
  rSquared: "r²",
  pairsN: "Pairs (n)",
  strength: "Strength",
  direction: "Direction",
  howThisWasCalculated: "How this was calculated",
  interpretation:
    "This is a {strength} {direction} linear correlation. r² = {rSquared}, so about {percent}% of the variance is shared between the two variables.",
  aNoteOfCaution: "A note of caution",
  caution:
    "Correlation is not causation — a strong r never proves that one variable causes the other. Pearson's r also only detects {linear} relationships and is sensitive to outliers, so a curved relationship can score near zero and a single extreme pair can distort the result. Plot your data before drawing conclusions.",
  cautionLinear: "linear",
} as const;

function interpretStrength(
  r: number,
  strings: typeof CORRELATION_CALCULATOR_STRINGS
): string {
  const abs = Math.abs(r);
  if (abs >= 0.7) {
    return strings.strong;
  }
  if (abs >= 0.4) {
    return strings.moderate;
  }
  if (abs >= 0.1) {
    return strings.weak;
  }
  return strings.negligible;
}

function interpretDirection(
  r: number,
  strings: typeof CORRELATION_CALCULATOR_STRINGS
): string {
  if (r > 0) {
    return strings.positive;
  }
  if (r < 0) {
    return strings.negative;
  }
  return strings.flat;
}

function formatNum(value: number, fmt: NumberFormatter): string {
  return fmt(value, { maximumFractionDigits: 4 });
}

const TEXTAREA_CLASS =
  "min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary";

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

function validationMessage(
  xs: number[],
  ys: number[],
  strings: typeof CORRELATION_CALCULATOR_STRINGS,
  locale: string
): string {
  if (xs.length !== ys.length) {
    return t(strings.lengthsMismatch, {
      xValues: tPlural(strings.valuesCount, locale, xs.length),
      yCount: ys.length,
    });
  }
  if (xs.length < 3) {
    return strings.enterAtLeast3Pairs;
  }
  return strings.xyMustVary;
}

export default function CorrelationCalculatorWidget() {
  const [entryMode, setEntryMode] = useState<EntryMode>("separate");
  const [xInput, setXInput] = useState("");
  const [yInput, setYInput] = useState("");
  const [pairedInput, setPairedInput] = useState("");

  const locale = useToolLocale();
  const strings = useToolUi(CORRELATION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();

  const separate = { xs: parseNumbers(xInput), ys: parseNumbers(yInput) };
  const paired = parsePairedLines(pairedInput);
  const { xs, ys } = entryMode === "separate" ? separate : paired;

  const hasInput = xs.length > 0 || ys.length > 0;
  const result = computePearson(xs, ys);

  return (
    <>
      {/* Entry mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.dataEntryMode}
        </p>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={entryMode === "separate"}
              className="accent-primary"
              name="corr-mode"
              onChange={() => setEntryMode("separate")}
              type="radio"
              value="separate"
            />
            {strings.separateXAndYLists}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={entryMode === "paired"}
              className="accent-primary"
              name="corr-mode"
              onChange={() => setEntryMode("paired")}
              type="radio"
              value="paired"
            />
            {strings.pairedOneXYPerLine}
          </label>
        </div>
      </section>

      {/* Inputs */}
      {entryMode === "separate" ? (
        <section className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="corr-x"
            >
              {strings.xValues}
            </label>
            <textarea
              className={TEXTAREA_CLASS}
              id="corr-x"
              onChange={(e) => setXInput(e.target.value)}
              placeholder={strings.xValuesPlaceholder}
              value={xInput}
            />
            <p className="text-muted-foreground text-xs">
              {tPlural(strings.valuesCount, locale, separate.xs.length, {
                count: fmt(separate.xs.length),
              })}
            </p>
          </div>
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="corr-y"
            >
              {strings.yValues}
            </label>
            <textarea
              className={TEXTAREA_CLASS}
              id="corr-y"
              onChange={(e) => setYInput(e.target.value)}
              placeholder={strings.yValuesPlaceholder}
              value={yInput}
            />
            <p className="text-muted-foreground text-xs">
              {tPlural(strings.valuesCount, locale, separate.ys.length, {
                count: fmt(separate.ys.length),
              })}
            </p>
          </div>
        </section>
      ) : (
        <section className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="corr-pairs"
          >
            {strings.pairedDataLabel}
          </label>
          <textarea
            className={TEXTAREA_CLASS}
            id="corr-pairs"
            onChange={(e) => setPairedInput(e.target.value)}
            placeholder={strings.pairedDataPlaceholder}
            value={pairedInput}
          />
          <p className="text-muted-foreground text-xs">
            {tPlural(strings.pairsDetected, locale, paired.xs.length, {
              count: fmt(paired.xs.length),
            })}
          </p>
        </section>
      )}

      {/* Validation */}
      {hasInput && !result && (
        <p className="rounded-lg border border-border bg-card p-4 text-muted-foreground text-sm">
          {validationMessage(xs, ys, strings, locale)}
        </p>
      )}

      {/* Results */}
      {result && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.results}
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard
              label={strings.pearsonsR}
              value={formatNum(result.r, fmt)}
            />
            <StatCard
              label={strings.rSquared}
              value={formatNum(result.rSquared, fmt)}
            />
            <StatCard label={strings.pairsN} value={fmt(result.n)} />
            <StatCard
              label={strings.strength}
              value={interpretStrength(result.r, strings)}
            />
            <StatCard
              label={strings.direction}
              value={interpretDirection(result.r, strings)}
            />
          </div>

          {/* Working */}
          <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
            <h3 className="font-semibold text-foreground text-lg">
              {strings.howThisWasCalculated}
            </h3>
            <p className="font-mono text-muted-foreground text-sm leading-relaxed">
              x̄ = {formatNum(result.meanX, fmt)}, ȳ ={" "}
              {formatNum(result.meanY, fmt)} (n = {fmt(result.n)})
            </p>
            <p className="font-mono text-muted-foreground text-sm leading-relaxed">
              Σ(x−x̄)(y−ȳ) = {formatNum(result.sxy, fmt)}, Σ(x−x̄)² ={" "}
              {formatNum(result.sxx, fmt)}, Σ(y−ȳ)² ={" "}
              {formatNum(result.syy, fmt)}
            </p>
            <p className="font-mono text-muted-foreground text-sm leading-relaxed">
              r = Σ(x−x̄)(y−ȳ) / √(Σ(x−x̄)² × Σ(y−ȳ)²) ={" "}
              {formatNum(result.sxy, fmt)} / √({formatNum(result.sxx, fmt)} ×{" "}
              {formatNum(result.syy, fmt)}) = {formatNum(result.sxy, fmt)} /{" "}
              {formatNum(Math.sqrt(result.sxx * result.syy), fmt)} ={" "}
              <strong className="text-foreground">
                {formatNum(result.r, fmt)}
              </strong>
            </p>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t(strings.interpretation, {
                strength: interpretStrength(result.r, strings),
                direction: interpretDirection(result.r, strings),
                rSquared: formatNum(result.rSquared, fmt),
                percent: formatNum(result.rSquared * 100, fmt),
              })}
            </p>
          </div>

          {/* Honest note */}
          <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-6">
            <h3 className="font-semibold text-foreground text-lg">
              {strings.aNoteOfCaution}
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {renderSlots(strings.caution, {
                linear: <em>{strings.cautionLinear}</em>,
              })}
            </p>
          </div>
        </section>
      )}
    </>
  );
}
